import { FreeMode } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { useEffect, useRef, useState } from "react";
import { useCompetitionCategories } from "../../redux/features/categories/selectors";
import { CompetitionCategory } from "../../redux/features/categories/slice";
import { cn } from "../../../lib/utils";

const CategoryMenu = () => {
  const competitionsCategories = useCompetitionCategories();
  const [active, setActive] = useState<CompetitionCategory["type"]>("");
  const swiperRef = useRef(null);
  useEffect(() => {
    const onScrollByCategory = (e: CustomEvent<CompetitionCategory["type"]>) => {
      const activeSlide = competitionsCategories.findIndex(category => category.type === e.detail);
      if (activeSlide !== -1) {
        swiperRef.current.slideTo(activeSlide);
      }
      setActive(e.detail);
    };

    window.addEventListener("scroll-by-category", onScrollByCategory);
    const updateHeaderHeight = () => {
      const header = document.getElementById("botb-header") || document.querySelector(".mainHeader");

      if (header) {
        const height = header.getBoundingClientRect().height;
        document.documentElement.style.setProperty("--header-height", `${height}px`);
      }
    };

    updateHeaderHeight();

    window.addEventListener("resize", updateHeaderHeight);

    const header = document.getElementById("botb-header") || document.querySelector(".mainHeader");

    if (header) {
      const observer = new MutationObserver(() => {
        updateHeaderHeight();
      });

      observer.observe(header, {
        childList: true,
        subtree: true,
        attributes: true,
      });

      const timeout = setTimeout(updateHeaderHeight, 1500);

      return () => {
        clearTimeout(timeout);
        observer.disconnect();
        window.removeEventListener("resize", updateHeaderHeight);
        window.removeEventListener("scroll-by-category", onScrollByCategory);
      };
    }
  }, [competitionsCategories]);

  const handleClick = (competition: CompetitionCategory) => {
    switch (competition.type) {
      case "subscriberCompetitions": {
        location.href = "/subscriber-competitions";
        break;
      }
      default: {
        const element = document.getElementById(competition.type);
        const header = document.getElementById("botb-header") || document.querySelector(".mainHeader");
        const categoryMenu = document.getElementById("category-menu");

        if (element && header && categoryMenu) {
          const headerHeight = header.getBoundingClientRect().height;
          const categoryMenuHeight = categoryMenu.getBoundingClientRect().height;
          const elementPosition = element.getBoundingClientRect().top + window.scrollY;
          const offsetPosition = elementPosition - headerHeight - categoryMenuHeight - 16;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });

          const scrollEvent = new CustomEvent("scroll-by-category", {
            detail: competition.type,
          });
          window.dispatchEvent(scrollEvent);

          setActive(competition.type);

          history.replaceState(null, "", location.pathname);
        }
      }
    }
  };

  return (
    <div
      id={"category-menu"}
      className={`w-full bg-white shadow-[0_1.5px_8px_0_rgba(0,0,0,0.20)] sticky z-40 ${competitionsCategories.length > 0 ? "" : "min-h-12 lg:min-h-[72px]"}`}
      style={{ top: `var(--header-height)` }}>
      <div className={"w-full max-w-[1328px] mx-auto"}>
        <Swiper
          onSwiper={swiper => {
            swiperRef.current = swiper;
          }}
          className={"max-lg:!px-2"}
          slidesPerView={"auto"}
          spaceBetween={0}
          modules={[FreeMode]}
          freeMode={true}>
          {competitionsCategories.map((item, index) => (
            <SwiperSlide
              key={`category-slide-${index}`}
              className="flex items-center justify-center min-h-auto !w-auto whitespace-nowrap mr-3 lg:mr-4 last:mr-0">
              <button
                type="button"
                onClick={() => handleClick(item)}
                className={cn(
                  "min-h-12 lg:min-h-[72px] flex-center gap-2 lg:gap-3 transition ease-in-out duration-200 font-jost text-sm font-medium leading-5 lg:text-base lg:leading-[22px] relative m-0 px-2 lg:px-3 border-b-2 border-solid cursor-pointer overflow-hidden text-secondary border-b-transparent",
                  {
                    "text-primary border-b-primary": active === item.type,
                  }
                )}>
                {item.imageUrl && (
                  <img
                    className="size-6 lg:size-8"
                    src={item.imageUrl}
                    alt={`icon ${item.type}`}
                    width={24}
                    height={24}
                  />
                )}
                {item.title}
              </button>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
};

export default CategoryMenu;
